/**
 * NarrativeContextCard - Collapsible card showing narrative context in NarrationView
 *
 * Features:
 * - Collapsed by default (shows compact header with tone badge)
 * - Expanded shows full theme, voice style, and editable TTS prompt
 * - Model-aware: shows Flash prompt when Flash selected, Pro when Pro selected
 * - Inline editing with save button for TTS prompts
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  ChevronDown,
  ChevronRight,
  Theater,
  Mic2,
  Save,
  Loader2,
  MessageSquare,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';

// Narrative context structure from video-scripts.json
export interface NarrativeContext {
  openingHook: string;
  keyTheme: string;
  tone: string;
  resolution: string;
  baseVoiceStyle?: {
    pace: string;
    personality: string;
    tone: string;
  };
  // Single TTS prompt for audio generation
  audioPrompt?: string;
  // Legacy fields for backward compatibility
  videoPromptFlash?: string;
  videoPromptPro?: string;
}

interface NarrativeContextCardProps {
  context: NarrativeContext | null;
  /** @deprecated Model selection no longer used - single TTS model is configured via env var */
  selectedModel?: 'flash' | 'pro';
  /** Callback when saving the audio prompt */
  onSavePrompt?: (prompt: string) => Promise<void>;
  onOpenChat?: () => void;
  isLoading?: boolean;
  className?: string;
}

export const NarrativeContextCard: React.FC<NarrativeContextCardProps> = ({
  context,
  selectedModel: _selectedModel, // Deprecated
  onSavePrompt,
  onOpenChat,
  isLoading = false,
  className,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Get the TTS prompt - prefer audioPrompt, fall back to legacy fields
  const activePrompt = context
    ? (context.audioPrompt || context.videoPromptPro || context.videoPromptFlash || '')
    : '';

  // Sync edited prompt when context changes
  useEffect(() => {
    setEditedPrompt(activePrompt);
    setIsEditing(false);
  }, [activePrompt]);

  // Handle save
  const handleSave = async () => {
    if (!onSavePrompt || editedPrompt === activePrompt) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSavePrompt(editedPrompt);
      setIsEditing(false);
      toast.success('TTS prompt saved');
    } catch (error) {
      console.error('Failed to save prompt:', error);
      toast.error('Failed to save prompt');
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel editing
  const handleCancel = () => {
    setEditedPrompt(activePrompt);
    setIsEditing(false);
  };

  // If no context and not loading, don't render
  if (!context && !isLoading) {
    return null;
  }

  return (
    <div className={cn(
      "rounded-lg border border-primary/30 overflow-hidden bg-white/50 dark:bg-zinc-900/50 backdrop-blur-sm",
      className
    )}>
      {/* Collapsed header - always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        disabled={isLoading}
        className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-accent/50 dark:hover:bg-accent/20 transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-primary dark:text-accent-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-primary dark:text-accent-foreground" />
        )}
        <Theater className="h-4 w-4 text-primary dark:text-accent-foreground" />
        <span className="text-sm font-medium text-gray-700 dark:text-zinc-300">
          Video Voice Style
        </span>

        {/* Loading state */}
        {isLoading && (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-primary ml-auto" />
        )}

        {/* Tone badge when collapsed */}
        {!isLoading && context?.tone && !isExpanded && (
          <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-accent text-primary dark:text-accent-foreground truncate max-w-[150px]">
            {context.tone}
          </span>
        )}
      </button>

      {/* Expanded content */}
      <AnimatePresence>
        {isExpanded && context && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 space-y-3">
              {/* Theme & Tone Section */}
              <div className="space-y-2">
                {context.keyTheme && (
                  <div className="flex items-start gap-2">
                    <span className="text-[10px] font-medium text-primary dark:text-accent-foreground uppercase tracking-wider w-12 pt-0.5 shrink-0">
                      Theme
                    </span>
                    <p className="text-xs text-gray-600 dark:text-zinc-400 leading-relaxed">
                      {context.keyTheme}
                    </p>
                  </div>
                )}
                {context.tone && (
                  <div className="flex items-start gap-2">
                    <span className="text-[10px] font-medium text-primary dark:text-accent-foreground uppercase tracking-wider w-12 pt-0.5 shrink-0">
                      Tone
                    </span>
                    <p className="text-xs text-gray-600 dark:text-zinc-400">
                      {context.tone}
                    </p>
                  </div>
                )}
              </div>

              {/* Voice Style Badges */}
              {context.baseVoiceStyle && (
                <div className="flex flex-wrap gap-1.5">
                  {context.baseVoiceStyle.pace && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-accent text-primary dark:text-accent-foreground">
                      {context.baseVoiceStyle.pace}
                    </span>
                  )}
                  {context.baseVoiceStyle.personality && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-accent text-primary dark:text-accent-foreground">
                      {context.baseVoiceStyle.personality}
                    </span>
                  )}
                  {context.baseVoiceStyle.tone && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-accent text-primary dark:text-accent-foreground">
                      {context.baseVoiceStyle.tone}
                    </span>
                  )}
                </div>
              )}

              {/* TTS Prompt Section */}
              <div className="pt-2 border-t border-primary/30 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Mic2 className="h-3.5 w-3.5 text-primary dark:text-accent-foreground" />
                    <span className="text-[10px] font-medium text-primary dark:text-accent-foreground uppercase tracking-wider">
                      TTS Prompt
                    </span>
                  </div>
                  {onSavePrompt && !isEditing && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditing(true)}
                      className="h-6 px-2 text-[10px] text-primary dark:text-accent-foreground hover:bg-accent dark:hover:bg-accent/50"
                    >
                      Edit
                    </Button>
                  )}
                </div>

                {isEditing ? (
                  <div className="space-y-2">
                    <Textarea
                      value={editedPrompt}
                      onChange={(e) => setEditedPrompt(e.target.value)}
                      className="text-xs font-mono min-h-[100px] bg-accent/50 border-primary/30 resize-none"
                      placeholder="Enter TTS prompt..."
                    />
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCancel}
                        disabled={isSaving}
                        className="h-7 px-2 text-xs"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={isSaving || editedPrompt === activePrompt}
                        className="h-7 px-3 text-xs bg-primary hover:bg-primary/90 text-primary-foreground"
                      >
                        {isSaving ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : (
                          <Save className="h-3 w-3 mr-1" />
                        )}
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-2 bg-accent/50 rounded text-xs text-gray-600 dark:text-zinc-400 whitespace-pre-wrap font-mono max-h-[120px] overflow-y-auto">
                    {activePrompt || 'No prompt configured'}
                  </div>
                )}
              </div>

              {/* Info footer */}
              <div className="flex items-start gap-1.5 pt-1">
                <Info className="h-3 w-3 text-primary shrink-0 mt-0.5" />
                <p className="text-[10px] text-primary dark:text-accent-foreground">
                  This voice style applies to all slides in this video
                </p>
              </div>

              {/* Edit via Chat button */}
              {onOpenChat && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onOpenChat}
                  className="w-full h-8 text-xs border-primary/30 text-primary dark:text-accent-foreground hover:bg-accent dark:hover:bg-accent/30"
                >
                  <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                  Edit via Chat
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NarrativeContextCard;
